import React, { useState } from "react";
import { motion } from "framer-motion";
import { ArrowRight, MapPin, Mail, Wrench } from "lucide-react";
import { Toaster, toast } from "sonner";

const FORMSPREE_ID = process.env.REACT_APP_FORMSPREE_ID || "DEINE_FORM_ID";

export const Contact = () => {
  const [form, setForm] = useState({
    name: "",
    email: "",
    event_type: "",
    event_date: "",
    message: "",
  });
  const [busy, setBusy] = useState(false);

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim() || !form.message.trim()) {
      toast.error("Bitte fülle Name, E-Mail und Nachricht aus.");
      return;
    }
    setBusy(true);
    try {
      const res = await fetch(`https://formspree.io/f/${FORMSPREE_ID}`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Accept: "application/json" },
        body: JSON.stringify({
          name: form.name,
          email: form.email,
          event_type: form.event_type,
          event_date: form.event_date,
          message: form.message,
        }),
      });
      if (!res.ok) throw new Error();
      toast.success("Anfrage gesendet — wir melden uns innerhalb von 24 Stunden.");
      setForm({ name: "", email: "", event_type: "", event_date: "", message: "" });
    } catch (err) {
      toast.error("Senden fehlgeschlagen. Bitte versuch es nochmal.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <section
      id="contact"
      data-testid="contact-section"
      data-glow="contact"
      className="relative z-10 px-6 sm:px-10 lg:px-14 py-28 lg:py-36 max-w-[1500px] mx-auto"
    >
      <Toaster
        theme="dark"
        position="bottom-center"
        toastOptions={{
          style: {
            background: "rgba(5,5,6,0.92)",
            border: "1px solid rgba(244,152,0,0.4)",
            color: "var(--offwhite)",
            borderRadius: 14,
          },
        }}
      />

      <motion.div
        initial={{ opacity: 0, y: 30 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: "-80px" }}
        transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
        className="relative rounded-[28px] p-8 sm:p-12 lg:p-16 overflow-hidden"
        style={{
          background:
            "linear-gradient(135deg, rgba(255,255,255,0.04) 0%, rgba(255,255,255,0.015) 100%)",
          backdropFilter: "blur(28px) saturate(140%)",
          WebkitBackdropFilter: "blur(28px) saturate(140%)",
          border: "1px solid rgba(244,152,0,0.18)",
          boxShadow:
            "inset 0 1px 0 rgba(255,255,255,0.05), 0 30px 80px -20px rgba(0,0,0,0.6)",
        }}
      >
        {/* Subtle inner glow */}
        <div
          className="absolute inset-0 pointer-events-none"
          style={{
            background:
              "radial-gradient(ellipse 70% 40% at 30% 0%, rgba(244,152,0,0.10), transparent 60%), radial-gradient(ellipse 60% 50% at 90% 100%, rgba(231,22,129,0.08), transparent 60%)",
          }}
        />

        <form
          onSubmit={submit}
          noValidate
          data-testid="contact-form"
          className="relative grid grid-cols-1 lg:grid-cols-[1.05fr_1fr] gap-10 lg:gap-16 items-start"
        >
          {/* LEFT — copy + contact details */}
          <div>
            <div
              className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full mb-7"
              style={{
                background: "rgba(255,255,255,0.04)",
                border: "1px solid rgba(244,152,0,0.22)",
              }}
            >
              <span
                className="w-1.5 h-1.5 rounded-full"
                style={{ background: "var(--amber)", boxShadow: "0 0 8px var(--amber)" }}
              />
              <span className="font-mono text-[11px] uppercase tracking-[0.22em] text-amber">
                Bereit loszulegen?
              </span>
            </div>

            <h2
              className="font-display font-bold leading-[1.0] tracking-[-0.035em] mb-7"
              style={{ fontSize: "clamp(44px, 5.5vw, 84px)" }}
            >
              Lass uns
              <br />
              über{" "}
              <span className="gradient-text">dein Event</span>
              <br />
              reden.
            </h2>

            <p className="body-mute text-base lg:text-lg max-w-[44ch] mb-10 leading-[1.65]">
              Egal ob große Veranstaltung oder intime Feier — wir freuen uns auf
              deine Anfrage. Gemeinsam machen wir dein Event unvergesslich.
            </p>

            <div className="space-y-4">
              <ContactRow icon={Mail} label="Anfragen" value="hello@monoton.events" href="mailto:hello@monoton.events" />
              <ContactRow icon={Wrench} label="Bestandskunden / Rental" value="rental@monoton.events" href="mailto:rental@monoton.events" />
              <ContactRow icon={MapPin} label="Standort" value="Blütenstraße 6 · 90480 Nürnberg" />
            </div>
          </div>

          {/* RIGHT — form fields */}
          <div className="space-y-5">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <Field label="Name" required>
                <input
                  className="fld"
                  type="text"
                  value={form.name}
                  onChange={update("name")}
                  placeholder="Max Mustermann"
                  data-testid="contact-name"
                />
              </Field>
              <Field label="E-Mail" required>
                <input
                  className="fld"
                  type="email"
                  value={form.email}
                  onChange={update("email")}
                  placeholder="max@beispiel.de"
                  data-testid="contact-email"
                />
              </Field>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <Field label="Art des Events">
                <select
                  className="fld appearance-none pr-9"
                  value={form.event_type}
                  onChange={update("event_type")}
                  data-testid="contact-eventtype"
                  style={{
                    backgroundImage:
                      "url(\"data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='12' height='8' viewBox='0 0 12 8'><path d='M1 1l5 5 5-5' stroke='%23F49800' stroke-width='1.5' fill='none' stroke-linecap='round'/></svg>\")",
                    backgroundRepeat: "no-repeat",
                    backgroundPosition: "right 14px center",
                  }}
                >
                  <option value="">Bitte wählen…</option>
                  <option>Hochzeit</option>
                  <option>Konzert</option>
                  <option>Corporate / Gala</option>
                  <option>Festival</option>
                  <option>Privatfeier</option>
                  <option>Andere</option>
                </select>
              </Field>
              <Field label="Datum (ca.)">
                <input
                  className="fld"
                  type="text"
                  value={form.event_date}
                  onChange={update("event_date")}
                  placeholder="z.B. 15.06.2026"
                  data-testid="contact-eventdate"
                />
              </Field>
            </div>

            <Field label="Deine Nachricht" required>
              <textarea
                className="fld resize-none"
                rows={6}
                value={form.message}
                onChange={update("message")}
                placeholder="Erzähl uns von deinem Event — Location, Gäste, Vorstellung."
                data-testid="contact-message"
              />
            </Field>

            <div className="pt-2">
              <button
                type="submit"
                disabled={busy}
                className="w-full inline-flex items-center justify-center gap-2 rounded-full font-display font-medium transition-all"
                style={{
                  background: "var(--offwhite)",
                  color: "var(--ink)",
                  padding: "18px 32px",
                  fontSize: 16,
                  boxShadow: "0 0 40px rgba(244,152,0,0.20)",
                  opacity: busy ? 0.7 : 1,
                  cursor: busy ? "wait" : "pointer",
                }}
                data-testid="contact-submit"
                onMouseEnter={(e) =>
                  !busy && (e.currentTarget.style.boxShadow = "0 0 60px rgba(244,152,0,0.55)")
                }
                onMouseLeave={(e) =>
                  (e.currentTarget.style.boxShadow = "0 0 40px rgba(244,152,0,0.20)")
                }
              >
                {busy ? "Wird gesendet…" : "Anfrage senden"} <ArrowRight size={16} />
              </button>
              <p className="text-center font-mono text-[11px] uppercase tracking-[0.2em] text-mute mt-4">
                Wir antworten innerhalb von 24 Stunden.
              </p>
            </div>
          </div>
        </form>
      </motion.div>
    </section>
  );
};

const Field = ({ label, required, children }) => (
  <label className="block">
    <span className="block font-display text-[13px] font-medium text-offwhite/85 mb-2">
      {label} {required && <span className="text-amber">*</span>}
    </span>
    {children}
  </label>
);

const ContactRow = ({ icon: Icon, label, value, href }) => {
  const Inner = (
    <div className="flex items-center gap-4 group">
      <span
        className="flex items-center justify-center shrink-0"
        style={{
          width: 44,
          height: 44,
          borderRadius: 12,
          background: "rgba(244,152,0,0.10)",
          border: "1px solid rgba(244,152,0,0.25)",
        }}
      >
        <Icon size={16} className="text-amber" strokeWidth={1.6} />
      </span>
      <span className="flex flex-col">
        <span className="font-mono text-[11px] uppercase tracking-[0.22em] text-mute">
          {label}
        </span>
        <span className="font-display text-[15px] text-offwhite group-hover:text-amber transition-colors mt-0.5">
          {value}
        </span>
      </span>
    </div>
  );
  return href ? <a href={href} className="block w-fit">{Inner}</a> : <div className="block w-fit">{Inner}</div>;
};
